"use client";

import { useActionState } from "react";
import { useTranslations } from "next-intl";
import LastUsedTag from "@/components/auth/LastUsedTag";
import { requestLoginLinkAction } from "@/lib/actions/login-link";
import { BusyLabel } from "@/components/ui/Spinner";

/**
 * "Email me a login link" — what makes a professional able to sign in whenever
 * they want, instead of asking the team to issue a link by hand every time.
 *
 * The reply is the SAME whatever happened (unknown address, an admin's address,
 * rate-limited, mail send failed). Anything else turns this box into an oracle
 * for "does this address have an account, and is it a professional?".
 */
export default function LoginLinkForm({ lastUsed = false }: { lastUsed?: boolean }) {
  const t = useTranslations("auth");
  const [state, action, pending] = useActionState(requestLoginLinkAction, undefined);

  const normalInput =
    "rounded-control border border-navy/20 bg-white px-13 py-8 text-sm text-navy placeholder:text-gray-500 focus-visible:border-gold focus-visible:outline-none";

  if (state?.status === "sent") {
    return (
      <p
        role="status"
        className="mt-21 rounded-card border border-card-line bg-surface px-13 py-13 text-sm text-gray-600"
      >
        {t("linkSent")}
      </p>
    );
  }

  return (
    <form action={action} className="mt-21 space-y-13" noValidate aria-label={t("emailMeLink")}>
      <p className="text-sm text-gray-600">{t("linkIntro")}</p>

      <div className="flex flex-col gap-3">
        <label
          htmlFor="link-email"
          className="text-xs font-semibold uppercase tracking-wider text-navy"
        >
          {t("emailLabel")}
        </label>
        <input
          id="link-email"
          type="email"
          name="email"
          required
          aria-required="true"
          autoComplete="email"
          placeholder="you@example.com"
          className={`w-full ${normalInput}`}
        />
      </div>

      <button
        type="submit"
        disabled={pending}
        aria-disabled={pending}
        className="relative w-full whitespace-nowrap rounded-control bg-gold py-13 text-sm font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-gold-hover disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        <BusyLabel busy={pending}>{pending ? t("linkSending") : t("linkSend")}</BusyLabel>
        {lastUsed && <LastUsedTag placement="onGold" />}
      </button>
    </form>
  );
}
